'use strict'

import SwatchDataCache from './SwatchDataCache'
import Utils from '../Utils.js'
import { DOM } from './DOM'
import { Selector } from './Selectors'
import { applyDefaultSwatchSelection } from './visualizer-texture-swatch-panel.js'
import { buildColorPagesData, buildTexturePagesData } from './SwatchPanelDataBuilder.js'

/**
 * SwatchPanel
 * Handles the color chart selector (swatch dataset) for the left panel.
 * Responsibilities:
 * - Load the selected swatch dataset from SwatchDataCache.
 * - Update swatch selection state.
 * - Render colors or texture-based swatches depending on the dataset.
 * - Emit viz:swatchDatasetLoaded for other UI modules (search, etc).
 */
class SwatchPanel {
    /**
     * @param {Object} params
     * @param {Object} params.app - Application instance (holds swatchSelection, canvasState, etc).
     * @param {HTMLSelectElement} params.selectEl - <select id="selectcc"> element.
     */
    constructor({ app, selectEl }) {
        this.app = app
        this.selectEl = selectEl
        this.dataCache = SwatchDataCache.getInstance()
    }

    /**
     * Bind the change handler to the swatch selector.
     * @returns {void}
     */
    bind() {
        if (!this.selectEl) return
        this.selectEl.addEventListener('change', (event) => {
            this.#handleChange(event)
        })
    }

    async #handleChange(event) {
        const app = this.app
        const swatchId = event?.target?.value
        if (!swatchId) return

        const swatch = await this.dataCache.getSwatchOrFetch(swatchId)
        app.swatchSelection.currentSwatch = swatch
        app.swatchSelection.currentSwatchId = swatch.id

        if (swatch.type === 1) {
            DOM.hide(Selector.textureswatchselectors)
            DOM.hide(Selector.scrollerEffects)
        }

        renderCurrentSwatchSelector(app)

        document.dispatchEvent(
            new CustomEvent('viz:swatchDatasetLoaded', {
                detail: { type: 'swatch', id: swatch.id },
            }),
        )
    }
}

/**
 * Render the color swatch grid.
 *
 * @param {Object} app
 * @returns {void}
 */
export const renderSwatchSelector = (app) => {
    const selection = app?.swatchSelection
    if (!selection) return

    const viewModel = buildColorPagesData(selection.currentSwatch)
    const hasTextureSwatches = Array.isArray(app?.listOfTextureSwatches) && app.listOfTextureSwatches.length > 0
    const isTouch = Utils.isTouchScreenDevice(navigator)

    app.swatchUiAdapter.renderColors({
        viewModel,
        hasTextureSwatches,
        isTouch,
    })

    app?.canvasState?.colorSample?.updateColorInfos?.()
    applyDefaultSwatchSelection(app)
    app.swatchUiAdapter.initDragAndDrop()
}

/**
 * Render the texture (image) swatch grid for the current swatch.
 *
 * @param {Object} app
 * @returns {void}
 */
export const renderTextureSelector = (app) => {
    const selection = app?.swatchSelection
    if (!selection) return

    const viewModel = buildTexturePagesData(selection.currentSwatch)
    app.swatchUiAdapter.renderTextures({
        viewModel,
    })

    app?.canvasState?.colorSample?.updateColorInfos?.()
    app.swatchUiAdapter.initDragAndDrop()
}

/**
 * Render the current swatch selection (colors or textures).
 *
 * @param {Object} app
 * @returns {void}
 */
export const renderCurrentSwatchSelector = (app) => {
    if (app?.swatchSelection?.hasSwatchTextureFilenames()) {
        renderTextureSelector(app)
    } else {
        renderSwatchSelector(app)
    }
}

export default SwatchPanel
